import {
  useQuery,
  UseQueryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { isAxiosError } from "axios";
import apiClient from "../apiClient";
import { customerRepresentativeKeys } from "./queryKeys";
import { receivablesKeys } from "./receivablesQueries";
import type {
  ContactPerson,
  CustomerOrganization,
  Payment,
} from "../../utils/interfaces";
import type { IntentionStatus } from "../../utils/types";
import { resolveAttachmentMimeType } from "../../utils/emailAttachments";

const BASE = "/customer_organizations";

export const CUSTOMER_NOTE_ATTACHMENT_MAX_FILES = 5;
export const CUSTOMER_NOTE_ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;

export type CustomerNoteAttachmentDisposition = "inline" | "attachment";

export type CustomerNoteAttachment = {
  id: number;
  filename: string;
  content_type: string;
  file_size_bytes: number;
  created_at: string;
};

const isNotFoundError = (error: unknown) =>
  isAxiosError(error) && error.response?.status === 404;

const noRetryOnNotFound = (failureCount: number, error: unknown) => {
  if (isNotFoundError(error)) return false;
  return failureCount < 3;
};

type CustomerDetailQueryOptions<T> = Pick<
  UseQueryOptions<T>,
  "enabled" | "staleTime" | "gcTime"
>;

type PaginatedResponse<T> = {
  items: T[];
  total: number;
  skip: number;
  limit: number;
};

type CustomerDetails = {
  notOverdueAmount: number;
  oneToThirtyDaysAmount: number;
  thirtyOneToSixtyDaysAmount: number;
  sixtyOneToNinetyDaysAmount: number;
  ninetyOneOrMoreDaysAmount: number;
  intentionStatus: IntentionStatus;
  total: number;
  currency: string;
  lastSentEmailDate?: string | null;
};

type EnrichedCustomerOrganization = CustomerOrganization & {
  enrichedDetails: CustomerDetails | null;
};

export const useAllCustomerRepresentativesQuery = (search?: string) =>
  useQuery({
    queryKey: customerRepresentativeKeys.list(search),
    queryFn: async () => {
      const searchParam = search ? `?search=${encodeURIComponent(search)}` : "";
      const { data } = await apiClient.get<
        PaginatedResponse<CustomerOrganization>
      >(`${BASE}/all${searchParam}`);
      return data;
    },
  });

export const useEnrichedCustomersQuery = (
  search?: string,
  intentionStatuses?: IntentionStatus[],
) =>
  useQuery({
    queryKey: [
      ...customerRepresentativeKeys.all,
      "enriched",
      search,
      intentionStatuses,
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (intentionStatuses?.length) {
        intentionStatuses.forEach((status) =>
          params.append("intention_status", status),
        );
      }
      const url = `${BASE}/enriched${params.toString() ? `?${params.toString()}` : ""}`;
      const { data } =
        await apiClient.get<PaginatedResponse<EnrichedCustomerOrganization>>(
          url,
        );
      return data;
    },
  });

export const useCustomerRepresentativeQuery = (
  id: number | undefined,
  options?: CustomerDetailQueryOptions<CustomerOrganization>,
) =>
  useQuery({
    queryKey:
      typeof id === "number" ? customerRepresentativeKeys.detail(id) : [],
    enabled: typeof id === "number" && (options?.enabled ?? true),
    staleTime: options?.staleTime,
    gcTime: options?.gcTime,
    retry: noRetryOnNotFound,
    queryFn: async () => {
      if (typeof id !== "number") throw new Error("id is required");
      const { data } = await apiClient.get<CustomerOrganization>(
        `${BASE}/${id}`,
      );
      return data;
    },
  });

export const useCustomerRepresentativeByPaymentIdQuery = (
  paymentExternalId: string | undefined,
  options?: Pick<
    UseQueryOptions<CustomerOrganization>,
    "enabled" | "staleTime" | "gcTime"
  >,
) =>
  useQuery({
    queryKey: paymentExternalId
      ? customerRepresentativeKeys.byPayment(paymentExternalId)
      : [],
    enabled: Boolean(paymentExternalId) && (options?.enabled ?? true),
    staleTime: options?.staleTime,
    gcTime: options?.gcTime,
    queryFn: async () => {
      if (!paymentExternalId) throw new Error("paymentExternalId is required");
      const { data } = await apiClient.get<CustomerOrganization>(
        `${BASE}/by_payment/${paymentExternalId}`,
      );
      return data;
    },
  });

export function customerPaymentsPath(
  id: number,
  includePaid = false,
): string {
  const path = `${BASE}/${id}/payments`;
  return includePaid ? `${path}?include_paid=true` : path;
}

export const useCustomerPaymentsQuery = (
  id: number | undefined,
  options?: CustomerDetailQueryOptions<Payment[]> & {
    includePaid?: boolean;
    placeholderData?: (previous: Payment[] | undefined) => Payment[] | undefined;
  },
) => {
  const includePaid = options?.includePaid ?? false;
  return useQuery({
    queryKey:
      typeof id === "number"
        ? [
            ...customerRepresentativeKeys.detail(id),
            "payments",
            includePaid ? "include_paid" : "open",
          ]
        : [],
    enabled: typeof id === "number" && (options?.enabled ?? true),
    staleTime: options?.staleTime,
    gcTime: options?.gcTime,
    placeholderData: options?.placeholderData,
    retry: noRetryOnNotFound,
    queryFn: async () => {
      if (typeof id !== "number") throw new Error("id is required");
      const { data } = await apiClient.get<Payment[]>(
        customerPaymentsPath(id, includePaid),
      );
      return data;
    },
  });
};

export const useCustomerSummaryQuery = (
  id: number | undefined,
  options?: CustomerDetailQueryOptions<CustomerDetails>,
) =>
  useQuery({
    queryKey:
      typeof id === "number"
        ? [...customerRepresentativeKeys.detail(id), "summary"]
        : [],
    enabled: typeof id === "number" && (options?.enabled ?? true),
    staleTime: options?.staleTime,
    gcTime: options?.gcTime,
    retry: noRetryOnNotFound,
    queryFn: async () => {
      if (typeof id !== "number") throw new Error("id is required");
      const { data } = await apiClient.get<CustomerDetails>(
        `${BASE}/${id}/summary`,
      );
      return data;
    },
  });

type UpdateCustomerSettingsVariables = {
  customerRepId: number;
  language: string;
  is_template_sending?: boolean;
};

export type CustomerNoteHistory = {
  id: number;
  customer_organization_id: number;
  payment_id?: number | null;
  body: string;
  author_id: number | null;
  author_name: string | null;
  created_at: string;
  attachments: CustomerNoteAttachment[];
};

function filenameFromContentDisposition(
  contentDisposition: string | undefined,
): string | null {
  if (!contentDisposition) return null;
  const match = contentDisposition.match(/filename="?([^"]+)"?/i);
  return match?.[1]?.trim() || null;
}

function attachmentBlobFromResponse(
  data: Blob,
  headers: Record<string, unknown>,
  filenameHint: string,
): Blob {
  const headerType =
    typeof headers["content-type"] === "string"
      ? headers["content-type"]
      : undefined;
  const filename =
    filenameFromContentDisposition(
      headers["content-disposition"] as string | undefined,
    ) ?? filenameHint;
  const type = resolveAttachmentMimeType(headerType, filename);
  if (data.type === type) {
    return data;
  }
  return new Blob([data], { type });
}

async function readBlobErrorDetail(data: unknown): Promise<string | null> {
  if (!(data instanceof Blob)) return null;
  try {
    const text = await data.text();
    if (!text.trim()) return null;
    const parsed = JSON.parse(text) as { detail?: unknown };
    if (typeof parsed.detail === "string" && parsed.detail.trim()) {
      return parsed.detail;
    }
    if (
      parsed.detail &&
      typeof parsed.detail === "object" &&
      "message" in parsed.detail
    ) {
      const message = (parsed.detail as { message?: unknown }).message;
      if (typeof message === "string" && message.trim()) {
        return message;
      }
    }
    return text;
  } catch {
    return null;
  }
}

export class CustomerNoteAttachmentFetchError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "CustomerNoteAttachmentFetchError";
    this.status = status;
  }
}

function getCustomerNoteAttachmentErrorMessage(
  status: number,
  detailMessage: string | null,
): string {
  if (detailMessage?.trim()) return detailMessage;
  if (status === 404) {
    return "This attachment is no longer available.";
  }
  if (status === 403) {
    return "You do not have permission to access this attachment.";
  }
  return "Could not load this attachment.";
}

export async function fetchCustomerNoteAttachmentBlob(
  customerOrgId: number,
  noteId: number,
  attachmentId: number,
  disposition: CustomerNoteAttachmentDisposition = "inline",
  signal?: AbortSignal,
  filenameHint = "attachment",
): Promise<Blob> {
  try {
    const response = await apiClient.get(
      `${BASE}/${customerOrgId}/notes/${noteId}/attachments/${attachmentId}`,
      {
        params: { disposition },
        responseType: "blob",
        signal,
      },
    );
    return attachmentBlobFromResponse(
      response.data as Blob,
      response.headers as Record<string, unknown>,
      filenameHint,
    );
  } catch (error) {
    const response = (
      error as { response?: { status?: number; data?: unknown } }
    ).response;
    const status = response?.status ?? 500;
    const detailMessage = await readBlobErrorDetail(response?.data);
    throw new CustomerNoteAttachmentFetchError(
      getCustomerNoteAttachmentErrorMessage(status, detailMessage),
      status,
    );
  }
}

export const useCustomerNotesHistoryQuery = (
  customerOrgId: number | undefined,
  options?: CustomerDetailQueryOptions<CustomerNoteHistory[]>,
) =>
  useQuery({
    queryKey:
      typeof customerOrgId === "number"
        ? customerRepresentativeKeys.notes(customerOrgId)
        : [],
    enabled: typeof customerOrgId === "number" && (options?.enabled ?? true),
    staleTime: options?.staleTime,
    gcTime: options?.gcTime,
    retry: noRetryOnNotFound,
    queryFn: async () => {
      if (typeof customerOrgId !== "number")
        throw new Error("customerOrgId is required");
      const { data } = await apiClient.get<CustomerNoteHistory[]>(
        `${BASE}/${customerOrgId}/notes`,
      );
      return data;
    },
  });

export const useAddCustomerNoteMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      customerRepId,
      body,
      files = [],
    }: {
      customerRepId: number;
      body?: string;
      files?: File[];
    }) => {
      const trimmedBody = body?.trim() ?? "";
      if (!trimmedBody && files.length === 0) {
        throw new Error("Note must include text or at least one attachment.");
      }

      const formData = new FormData();
      if (trimmedBody) {
        formData.append("body", trimmedBody);
      }
      for (const file of files) {
        formData.append("files", file);
      }

      const { data } = await apiClient.post<CustomerNoteHistory>(
        `${BASE}/${customerRepId}/notes`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
      return data;
    },
    onSuccess: async (_, { customerRepId }) => {
      await invalidateCustomerNotesQueries(queryClient, customerRepId);
    },
  });
};

async function invalidateCustomerNotesQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  customerRepId: number,
) {
  await queryClient.invalidateQueries({
    queryKey: customerRepresentativeKeys.notes(customerRepId),
  });
  await queryClient.invalidateQueries({
    queryKey: customerRepresentativeKeys.all,
  });
  await queryClient.invalidateQueries({ queryKey: receivablesKeys.all });
  await queryClient.invalidateQueries({
    queryKey: customerRepresentativeKeys.detail(customerRepId),
  });
}

export const useUpdateCustomerNoteMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      customerRepId,
      noteId,
      body,
    }: {
      customerRepId: number;
      noteId: number;
      body: string;
    }) => {
      const { data } = await apiClient.patch<CustomerNoteHistory>(
        `${BASE}/${customerRepId}/notes/${noteId}`,
        { body },
      );
      return data;
    },
    onSuccess: async (_, { customerRepId }) => {
      await invalidateCustomerNotesQueries(queryClient, customerRepId);
    },
  });
};

export const useDeleteCustomerNoteMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      customerRepId,
      noteId,
    }: {
      customerRepId: number;
      noteId: number;
    }) => {
      await apiClient.delete(`${BASE}/${customerRepId}/notes/${noteId}`);
    },
    onSuccess: async (_, { customerRepId }) => {
      await invalidateCustomerNotesQueries(queryClient, customerRepId);
    },
  });
};

export const useUpdateCustomerSettingsMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      customerRepId,
      language,
      is_template_sending,
    }: UpdateCustomerSettingsVariables) => {
      const { data } = await apiClient.patch<CustomerOrganization>(
        `${BASE}/${customerRepId}`,
        { language, is_template_sending },
      );
      return data;
    },
    onSuccess: async (_, { customerRepId }) => {
      await queryClient.invalidateQueries({
        queryKey: customerRepresentativeKeys.all,
      });
      await queryClient.invalidateQueries({
        queryKey: customerRepresentativeKeys.detail(customerRepId),
      });
    },
  });
};

/** @deprecated Use useUpdateCustomerSettingsMutation for language/settings only. */
export const useUpdateCustomerRepresentativeDescriptionMutation =
  useUpdateCustomerSettingsMutation;

type UpdateEmailVariables = { customerRepId: number; email: string | null };

export const useUpdateCustomerEmailMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ customerRepId, email }: UpdateEmailVariables) => {
      const { data } = await apiClient.patch<CustomerOrganization>(
        `${BASE}/${customerRepId}/email`,
        { email },
      );
      return data;
    },
    onSuccess: async (_, { customerRepId }) => {
      await queryClient.invalidateQueries({
        queryKey: customerRepresentativeKeys.all,
      });
      await queryClient.invalidateQueries({
        queryKey: customerRepresentativeKeys.detail(customerRepId),
      });
    },
  });
};

type CustomerAccountingDetails = {
  id: string | null;
  name: string | null;
  email: string | null;
  accounting_software: string | null;
};

export const useCustomerAccountingDetailsQuery = (
  customerRepId: number | undefined,
  options?: Pick<
    UseQueryOptions<CustomerAccountingDetails>,
    "enabled" | "staleTime" | "gcTime"
  >,
) =>
  useQuery({
    queryKey:
      typeof customerRepId === "number"
        ? [
            ...customerRepresentativeKeys.detail(customerRepId),
            "accounting_details",
          ]
        : [],
    enabled: typeof customerRepId === "number" && (options?.enabled ?? true),
    staleTime: options?.staleTime,
    gcTime: options?.gcTime,
    queryFn: async () => {
      if (typeof customerRepId !== "number")
        throw new Error("customerRepId is required");
      const { data } = await apiClient.get<CustomerAccountingDetails>(
        `${BASE}/${customerRepId}/accounting_details`,
      );
      return data;
    },
  });

type CreateContactVariables = {
  orgId: number;
  name: string;
  email: string;
  phone_number?: string | null;
  receives_automated_emails?: boolean;
  position_code?: number | null;
  is_escalation_contact?: boolean;
};

export const useCreateContactMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ orgId, ...body }: CreateContactVariables) => {
      const { data } = await apiClient.post<ContactPerson>(
        `${BASE}/${orgId}/contacts`,
        body,
      );
      return data;
    },
    onSuccess: async (_, { orgId }) => {
      await queryClient.invalidateQueries({
        queryKey: customerRepresentativeKeys.detail(orgId),
      });
    },
  });
};

type UpdateContactVariables = {
  orgId: number;
  contactId: number;
  name?: string;
  email?: string;
  phone_number?: string | null;
  receives_automated_emails?: boolean;
  position_code?: number | null;
  is_escalation_contact?: boolean;
};

export const useUpdateContactMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      orgId,
      contactId,
      ...body
    }: UpdateContactVariables) => {
      const { data } = await apiClient.patch<ContactPerson>(
        `${BASE}/${orgId}/contacts/${contactId}`,
        body,
      );
      return data;
    },
    onSuccess: async (_, { orgId }) => {
      await queryClient.invalidateQueries({
        queryKey: customerRepresentativeKeys.detail(orgId),
      });
    },
  });
};

export const useDeleteContactMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      orgId,
      contactId,
    }: {
      orgId: number;
      contactId: number;
    }) => {
      await apiClient.delete(`${BASE}/${orgId}/contacts/${contactId}`);
    },
    onSuccess: async (_, { orgId }) => {
      await queryClient.invalidateQueries({
        queryKey: customerRepresentativeKeys.detail(orgId),
      });
    },
  });
};

export const useSetPrimaryContactMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      orgId,
      contactId,
    }: {
      orgId: number;
      contactId: number;
    }) => {
      const { data } = await apiClient.patch<ContactPerson>(
        `${BASE}/${orgId}/contacts/${contactId}/primary`,
      );
      return data;
    },
    onSuccess: async (_, { orgId }) => {
      await queryClient.invalidateQueries({
        queryKey: customerRepresentativeKeys.detail(orgId),
      });
    },
  });
};

export const useBlockedCustomersQuery = () =>
  useQuery({
    queryKey: customerRepresentativeKeys.blocked(),
    queryFn: async () => {
      const { data } = await apiClient.get<PaginatedResponse<CustomerOrganization>>(
        `${BASE}/blocked`,
      );
      return data;
    },
  });

export const useBlockCustomerMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (customerId: number) => {
      const { data } = await apiClient.post<CustomerOrganization>(
        `${BASE}/${customerId}/block`,
      );
      return data;
    },
    onSuccess: async (_data, customerId) => {
      await queryClient.cancelQueries({
        queryKey: customerRepresentativeKeys.detail(customerId),
      });
      queryClient.removeQueries({
        queryKey: customerRepresentativeKeys.detail(customerId),
      });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: customerRepresentativeKeys.all }),
        queryClient.invalidateQueries({ queryKey: receivablesKeys.all }),
        queryClient.invalidateQueries({
          queryKey: customerRepresentativeKeys.blocked(),
        }),
      ]);
    },
  });
};

export const useUnblockCustomerMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (customerId: number) => {
      const { data } = await apiClient.post<CustomerOrganization>(
        `${BASE}/${customerId}/unblock`,
      );
      return data;
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: customerRepresentativeKeys.all }),
        queryClient.invalidateQueries({ queryKey: receivablesKeys.all }),
      ]);
    },
  });
};
